import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, forwardRef, Input, NgZone, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LocationsApiService } from '@shared-features/services/locations-api.service';
import { fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil, } from 'rxjs/operators';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { SectionState } from '@shared-features/enums';

@Component({
  selector: 'app-autocomplete-google-maps',
  templateUrl: './autocomplete-google-maps.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteGoogleMapsComponent),
      multi: true
    }
  ]
})
export class AutocompleteGoogleMapsComponent extends BaseComponent implements AfterViewInit, ControlValueAccessor {
  @Input() placeholder = '';
  
  @ViewChild('searchAddressInput') searchAddressInput: ElementRef<HTMLInputElement>;
  @ViewChild('searchAddressInput', { read: MatAutocompleteTrigger }) autoComplete: MatAutocompleteTrigger;

  private autocompleteService: google.maps.places.AutocompleteService;
  private placesService: google.maps.places.PlacesService;

  predictions: google.maps.places.AutocompletePrediction[] = [];

  private onChange: (value: google.maps.places.PlaceResult) => void = () => { };
  private onTouched: () => void = () => { };

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly locationsApiService: LocationsApiService,
    private readonly ngZone: NgZone,
  ) {
    super()
  }


  writeValue(obj: google.maps.places.PlaceResult): void {
    if (this.searchAddressInput) {
      this.setInputValue(obj);
    } else {
      // Defer if view not ready
      setTimeout(() => {
        if (this.searchAddressInput) {
          this.setInputValue(obj);
        }
      }, 100);
    }
  }

  private setInputValue(obj: google.maps.places.PlaceResult) {
    this.searchAddressInput.nativeElement.value = obj?.formatted_address ?? '';
  }


  registerOnChange(fn: (value: google.maps.places.PlaceResult) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  ngAfterViewInit(): void {
    this.handleAutocompletePosition();
    this.locationsApiService.loadGoogleMapsApi().then(() => {
      this.autocompleteService = new google.maps.places.AutocompleteService();
      this.placesService = new google.maps.places.PlacesService(document.createElement('div'));
      this.onSearchValueChange();
    });
  }


  private onSearchValueChange() {
    fromEvent(this.searchAddressInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      ).subscribe(() => {
        if (!this.searchAddressInput.nativeElement.value) {
          this.predictions = [];
        }
        this.getPredictions(this.searchAddressInput.nativeElement.value);
      });
  }

  private handleAutocompletePosition(): void {
    fromEvent(window, 'scroll', { capture: true })
      .pipe(
        filter(() => this.autoComplete.panelOpen),
        takeUntil(this.destroy$)
      ).subscribe(() => this.autoComplete.updatePosition());
  }

  getPredictions(input: string): void {
    if (!input.length) return;
    this.sectionState = SectionState.LoadingTransparent;
    this.changeDetector.markForCheck();
    this.autocompleteService.getPlacePredictions(
      {
        input,
        types: ['geocode'],
      },
      (predictions, status) => {
        this.ngZone.run(() => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            this.predictions = predictions;
          } else {
            this.predictions = [];
          }
          this.sectionState = SectionState.Ready;
          this.changeDetector.markForCheck();
        });
      });
  }


  selectPlace(event: MatAutocompleteSelectedEvent) {
    const selectedPrediction: google.maps.places.AutocompletePrediction = event.option.value;

    this.predictions = [];
    this.searchAddressInput.nativeElement.value = selectedPrediction.description;
    this.placesService.getDetails({ placeId: selectedPrediction.place_id }, (place, status) => {
      this.ngZone.run(() => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place?.geometry) {
          this.onChange(place);
          this.onTouched();
        } else {
          this.onChange(null);
        }
        this.changeDetector.detectChanges();
      });
    }
    );
  }

}

